<template>
    <pc-page-layout
        v-bind="layoutBinder"
        ref="layout">
        <pc-loader v-bind:loading="loading">
            <div v-if="!loading">
                <pc-page-header
                    icon-class="menu-incentive_design"
                    page-name="IncentiveDesignList" />
                <hr />
                <pc-tabs
                    id="tabs"
                    ref="tabs"
                    save-state
                    state-key="incentive-design-list"
                    hide-single>
                    <pc-tab
                        tab-id="IncentiveDesignsTab"
                        v-bind:title="incentiveDesignsTabLabel">
                        <incentive-designs-tab
                            ref="incentiveDesignsTab"
                            v-bind:designs="designs"
                            v-bind:approval-status-id="approvalStatusId"
                            v-bind:metadata-columns="metadataColumns"
                            v-bind:has-cpadmin-write-privilege="hasCPAdminWritePrivilege"
                            v-on:new="newDesign"
                            v-on:view="viewDesign"
                            v-on:edit="editDesign"
                            v-on:cpadminedit="cpadminEditDesign"
                            v-on:copy="copyDesign"
                            v-on:setexpirationdate="setExpirationDate"
                            v-on:enable="enableDesign"
                            v-on:disable="disableDesign"
                            v-on:delete="deleteDesign" />
                    </pc-tab>
                    <pc-tab
                        tab-id="IncentiveOptionsTab"
                        v-bind:title="incentiveOptionsTabLabel">
                        <incentive-options-tab
                            ref="incentiveOptionsTab"
                            v-bind:workflow-statuses="workflowStatuses"
                            v-bind:approval-status-id="approvalStatusId"
                            v-bind:metadata-columns="metadataColumns"
                            v-on:changeapprovalstatus="changeApprovalStatus"
                            v-on:addmetadata="addMetadata"
                            v-on:editmetadata="editMetadata"
                            v-on:deletemetadata="deleteMetadata" />
                    </pc-tab>
                </pc-tabs>
            </div>
        </pc-loader>
    </pc-page-layout>
</template>

<script>
    import pageBase from "Components/page/_page-base.js";
    import incentiveDesignsTab from "./incentiveDesignsTab.vue";
    import incentiveOptionsTab from "./incentiveOptionsTab.vue";
    import enableIncentiveDesignDialog from "./enableIncentiveDesignDialog.vue";
    import setIncentiveDesignExpirationDateDialog from "./setIncentiveDesignExpirationDateDialog.vue";
    import editIncentiveMetadataFieldDialog from "./editIncentiveMetadataFieldDialog.vue";

    import { mapI18n } from "Util/i18n";
    import { KEYS, NAMESPACE } from "./i18n/incentiveDesignList.i18n";
    import { KEYS as ENABLEKEYS, NAMESPACE as ENABLENAMESPACE } from "./i18n/enableIncentiveDesignDialog.i18n";
    import { DIALOG_RESULT } from "Components/pc-dialog.vue";

    export const MAX_INCENTIVE_METADATA_FIELDS = 3;
    export const METADATA_TYPE = {
        Text: "1",
        Decimal: "2"
    };
    export const NULL_TIMESTAMP_DISPLAY = "";

    export default {
        name: "IncentiveDesignList",
        components: { incentiveDesignsTab, incentiveOptionsTab },
        extends: pageBase,
        setup: pageBase.setup,
        data() {
            return {
                designs: [],
                workflowStatuses: [],
                approvalStatusId: "",
                incentiveSchemaGroupId: "",
                metadataColumns: [],
                hasCPAdminWritePrivilege: false,
                editIncentiveDesignUrl: null
            };
        },
        computed: {
            ...mapI18n(
                {
                    ...KEYS
                },
                NAMESPACE
            ),
            ...mapI18n(
                {
                    ...ENABLEKEYS
                },
                ENABLENAMESPACE
            )
        },
        methods: {
            onLoaded(model) {
                this.designs = model.IncentiveDesigns;
                this.workflowStatuses = model.WorkflowStatuses;
                this.approvalStatusId = model.ApprovalStatusId;
                this.incentiveSchemaGroupId = model.IncentiveSchemaGroupId;
                this.metadataColumns = model.MetadataColumns;
                this.hasCPAdminWritePrivilege = model.HasCPAdminWritePrivilege;
                this.editIncentiveDesignUrl = model.EditIncentiveDesignUrl;
                this.loading = false;
            },
            editDesignHref(designId, viewParam) {
                return this.editIncentiveDesignUrl + "&IncentiveDesignId=" + designId + "&ViewOnly=" + viewParam;
            },
            async newDesign() {
                const href = this.editDesignHref("-1", "0");
                this.$app.setHref(href);
            },
            async viewDesign(designId) {
                const href = this.editDesignHref(designId, "1");
                this.$app.setHref(href);
            },
            async editDesign(designId) {
                const href = this.editDesignHref(designId, "0");
                this.$app.setHref(href);
            },
            async cpadminEditDesign(designId) {
                const href = this.editDesignHref(designId, "0");
                this.$app.setHref(href);
            },
            async copyDesign(designId) {
                try {
                    this.blockUi(this.copyDesignInProgressText);
                    await this.CopyIncentiveDesign({
                        programId: this.ProgramId,
                        incentiveDesignId: designId
                    });
                    this.$app.reloadWindow();
                } finally {
                    this.unBlockUi();
                }
            },
            async setExpirationDate(designId) {
                const design = this.designs.find((d) => d.PublicId === designId);
                if (design === null) return;

                const dialogResult = await this.$app.showDialog({
                    title: this.setExpirationDateDialogTitle,
                    customComponent: setIncentiveDesignExpirationDateDialog,
                    customComponentOptions: {
                        startDateString: design.StartDateTimestamp.DateDisplay,
                        expirationDateString: design.ExpirationDateTimestamp.DateDisplay
                    },
                    customComponentResultHook: "resultHook",
                    customComponentIsValidToCloseHookAsync: "verifyCanClose",
                    buttons: [
                        {
                            id: "CancelExpirationDateButton",
                            text: this.setExpirationDateCancelButtonText,
                            result: DIALOG_RESULT.cancel
                        },
                        {
                            id: "SaveExpirationDateButton",
                            text: this.setExpirationDateSaveButtonText,
                            primary: true,
                            result: DIALOG_RESULT.ok
                        }
                    ]
                });
                if (dialogResult.action === DIALOG_RESULT.cancel) {
                    return;
                }
                if (dialogResult.data.NewExpirationDateString === design.ExpirationDateTimestamp.DateDisplay) {
                    return;
                }
                try {
                    this.blockUi(this.setExpirationDateInProgressText);
                    const result = await this.EditExpirationDate({
                        programId: this.ProgramId,
                        incentiveDesignId: designId,
                        expDateString: dialogResult.data.NewExpirationDateString
                    });
                    design.ExpirationDateTimestamp = result.Data;
                } finally {
                    this.unBlockUi();
                }
            },
            async updateDesignStatus(designId, enable) {
                const design = this.designs.find((d) => d.PublicId === designId);
                if (design === null) return;

                const title = enable ? this.enableDialogTitle : this.disableDialogTitle;
                const cancelButtonId = enable ? "EnableCancelButton" : "DisableCancelButton";
                const okButtonId = enable ? "EnableConfirmButton" : "DisableConfirmButton";
                const cancelButtonText = enable ? this.enableCancelButtonText : this.disableCancelButtonText;
                const okButtonText = enable ? this.enableOkButtonText : this.disableOkButtonText;

                const dialogResult = await this.$app.showDialog({
                    title: title,
                    customComponent: enableIncentiveDesignDialog,
                    customComponentOptions: {
                        designName: design.Name,
                        enable: enable
                    },
                    buttons: [
                        {
                            id: cancelButtonId,
                            text: cancelButtonText,
                            result: DIALOG_RESULT.cancel
                        },
                        {
                            id: okButtonId,
                            text: okButtonText,
                            primary: true,
                            result: DIALOG_RESULT.ok
                        }
                    ]
                });
                if (dialogResult === DIALOG_RESULT.ok) {
                    try {
                        const message = enable ? this.enableDesignInProgressText : this.disableDesignInProgressText;
                        this.blockUi(message);
                        await this.EnableIncentiveDesign({
                            programId: this.ProgramId,
                            incentiveDesignId: designId,
                            isEnabled: enable
                        });
                        design.IsEnabled = enable;
                        if (enable) {
                            design.IsEditable = false;
                        }
                    } finally {
                        this.unBlockUi();
                    }
                }
            },
            async enableDesign(designId) {
                this.updateDesignStatus(designId, true);
            },
            async disableDesign(designId) {
                this.updateDesignStatus(designId, false);
            },
            async deleteDesign(designId) {
                if (!designId) {
                    return;
                }

                const confirmDelete = await this.showConfirmationDialog(
                    this.designDeleteDialogTitle,
                    this.designDeleteConfirmText
                );

                if (!confirmDelete) {
                    return;
                }

                try {
                    this.blockUi(this.deleteDesignInProgressText);
                    await this.DeleteIncentiveDesign({
                        programId: this.ProgramId,
                        incentiveDesignId: designId
                    });
                    this.$app.reloadWindow();
                } finally {
                    this.$app.unBlockUi();
                }
            },
            async changeApprovalStatus(statusId, callback) {
                const dialogTitle = statusId
                    ? this.changeApprovalStatusDialogTitle
                    : this.removeApprovalStatusDialogTitle;
                const dialogMsg = statusId ? this.changeApprovalStatusDialogMsg : this.removeApprovalStatusDialogMsg;
                if (!(await this.showConfirmationDialog(dialogTitle, dialogMsg))) {
                    callback();
                    return;
                }

                try {
                    this.blockUi(this.updateApprovalStatusInProgressText);
                    await this.SetApprovalStatus({
                        programId: this.ProgramId,
                        statusId: statusId
                    });
                    this.approvalStatusId = statusId;
                } finally {
                    this.unBlockUi();
                }
            },
            async editMetadataField(metadataId) {
                const metadataColumn = this.metadataColumns.find((m) => m.PublicId === metadataId);
                const metadataName = metadataColumn ? metadataColumn.SchemaRowName : "";
                const metadataType = metadataColumn ? metadataColumn.DataFieldTypeConstantId : "";
                const metadataOrder = (
                    metadataColumn ? metadataColumn.OrderNumber : this.metadataColumns.length + 1
                ).toString();
                const isNew = metadataId === "";

                const title = metadataId ? this.metadataEditDialogTitle : this.metadataAddDialogTitle;

                const maxOrderNumber = Math.min(
                    this.metadataColumns.length + (isNew ? 1 : 0),
                    MAX_INCENTIVE_METADATA_FIELDS
                );

                const dialogResult = await this.$app.showDialog({
                    title: title,
                    customComponent: editIncentiveMetadataFieldDialog,
                    customComponentOptions: {
                        metadataName: metadataName,
                        metadataType: metadataType,
                        enableTypeInput: isNew,
                        metadataOrder: metadataOrder,
                        maxOrderNumber: maxOrderNumber
                    },
                    customComponentResultHook: "resultHook",
                    customComponentIsValidToCloseHookAsync: "verifyCanClose",
                    buttons: [
                        {
                            text: this.metadataEditDialogCancelButtonText,
                            result: DIALOG_RESULT.cancel
                        },
                        {
                            text: this.metadataEditDialogSaveButtonText,
                            primary: true,
                            result: DIALOG_RESULT.ok
                        }
                    ]
                });
                if (dialogResult.action === DIALOG_RESULT.cancel) {
                    return;
                }

                try {
                    this.blockUi(this.metadataEditInProgressText);
                    const result = await this.EditMetadataRow({
                        programId: this.ProgramId,
                        schemaGroupId: this.incentiveSchemaGroupId,
                        fieldId: metadataId,
                        dataFieldName: dialogResult.data.NewName,
                        dataTypeString: dialogResult.data.NewType,
                        orderNumberString: dialogResult.data.NewOrder
                    });
                    this.metadataColumns = result.Data;
                } finally {
                    this.unBlockUi();
                }
            },
            async addMetadata() {
                this.editMetadataField("");
            },
            async editMetadata(metadataId) {
                this.editMetadataField(metadataId);
            },
            async deleteMetadata(metadataId) {
                if (!metadataId) {
                    return;
                }

                const confirmDelete = await this.showConfirmationDialog(
                    this.metadataDeleteDialogTitle,
                    this.metadataDeleteConfirmText
                );

                if (!confirmDelete) {
                    return;
                }

                try {
                    this.blockUi(this.metadataDeleteInProgressText);
                    await this.DeleteMetadataRow({
                        programId: this.ProgramId,
                        fieldId: metadataId
                    });
                    const metadataIndex = this.metadataColumns.findIndex((c) => c.PublicId === metadataId);
                    this.metadataColumns.splice(metadataIndex, 1);
                    for (let i = metadataIndex; i < this.metadataColumns.length; i++) {
                        this.metadataColumns[i].OrderNumber = i + 1;
                    }
                } finally {
                    this.unBlockUi();
                }
            }
        }
    };
</script>
